import { Link } from "react-router-dom";
import { Loader2, Undo2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ReviseCard = ({
  result, access, reviseInstruction, setReviseInstruction,
  reviseLoading, reviseRemaining, undoLoading, onRevise, onUndo,
}) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="research-revise-card">
    <div className="flex items-center justify-between gap-3 mb-1">
      <h3 className="text-lg font-semibold text-slate-950 flex items-center gap-2">
        <Wand2 className="w-4 h-4 text-orange-600" /> Revise this draft
      </h3>
      <div className="flex items-center gap-3">
        {result.revision_count > 0 && (
          <button
            type="button"
            onClick={onUndo}
            disabled={undoLoading || reviseLoading}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50"
            data-testid="research-undo-revision-btn"
          >
            {undoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Undo last revision
          </button>
        )}
        {result.revision_count > 0 && (
          <span className="text-xs text-slate-400" data-testid="research-revision-count">
            {result.revision_count} revision{result.revision_count === 1 ? "" : "s"} applied
          </span>
        )}
        {reviseRemaining !== null && (
          <span className="text-xs text-slate-400" data-testid="research-revise-remaining">
            {reviseRemaining} left today
          </span>
        )}
      </div>
    </div>
    <p className="text-xs text-slate-500 mb-3">
      Tell the AI editor what to change — it keeps the draft grounded on the same library sources.
    </p>
    {access?.level === "free" ? (
      <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="research-revise-locked">
        <p className="text-sm text-orange-900">AI revisions are included with a subscription.</p>
        <Button asChild size="sm" className="bg-orange-600 hover:bg-orange-700 text-white">
          <Link to="/subscriptions">View plans</Link>
        </Button>
      </div>
    ) : (
    <>
    {!reviseLoading && !reviseInstruction && (
      <div className="flex flex-wrap gap-2 mb-3" data-testid="research-revise-suggestions">
        {["Expand the methodology section", "Make the abstract more concise", "Strengthen the conclusion", "Simplify the language"].map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => setReviseInstruction(suggestion)}
            className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs text-orange-800 hover:bg-orange-100"
          >
            {suggestion}
          </button>
        ))}
      </div>
    )}
    <div className="flex flex-col sm:flex-row gap-2">
      <textarea
        value={reviseInstruction}
        onChange={(e) => setReviseInstruction(e.target.value)}
        placeholder='e.g. "Expand the methods section with a detailed comparison of leaching agents, operating temperatures, and acid concentrations. Add a short paragraph on environmental trade-offs in the discussion."'
        maxLength={4000}
        rows={3}
        disabled={reviseLoading}
        className="flex-1 px-3 py-2 rounded-md border border-slate-300 text-sm leading-6 resize-y focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-slate-50"
        data-testid="research-revise-input"
      />
      <div className="flex sm:flex-col items-end justify-between gap-2">
        <Button
          onClick={onRevise}
          disabled={reviseLoading || reviseInstruction.trim().length < 5}
          className="h-10 bg-slate-900 hover:bg-slate-700"
          data-testid="research-revise-btn"
        >
          {reviseLoading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Revising…</> : <><Wand2 className="w-4 h-4 mr-1.5" />Revise</>}
        </Button>
        <span className="text-[10px] text-slate-400">{reviseInstruction.length}/4000</span>
      </div>
    </div>
    {reviseLoading && (
      <p className="text-[11px] text-slate-400 mt-2">The editor is rewriting your draft — this usually takes 30–60 seconds. The result replaces the draft above.</p>
    )}
    </>
    )}
  </div>
);
